import type { MenuDefinition } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import loc from 'owa-localize';
import Strings from 'owa-locstrings/lib/strings/ribbon_mail_buttons_and_tabs.locstring.json';
import { getMainWindowComposeViewState } from 'owa-mail-compose-actions/lib/selectors/getMainWindowComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import { ActionControlId, MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import {
    onAddressButtonClicked,
    onFlagUnflagClicked,
    onSaveDraft,
    onShowInImmersiveReaderClicked,
} from '../../../util/commandActions';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
    createMailAppSplitButton,
    createMailAppToggleButton,
} from '../../../util/createMailRibbonButton';
import { isImportance, onImportanceToggled } from '../../../util/importanceCommandActions';
import {
    getAssignPolicySections,
    getEncryptSingleButton,
    getFlagUnflagSections,
    getProtectEncryptSections,
    getSensitivitySections,
} from '../../sections';
import { getSensitivityMenu } from 'owa-compose-actioncontrols/lib/components/controls/sensitivityMenu';

// Placeholder for required onExecute parameter, being used temporarily
// until stubbed out ribbon buttons have been hooked up.
const unhandledOnExecute = () => {};

// Placeholder for required menuDefinition parameter, being used temporarily
// until stubbed out ribbon flyouts have been hooked up
const emptyMenuDefinition: MenuDefinition = {
    sections: [],
};

export const getFormatPainterControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.FormatPainter);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.FormatPainter, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            unhandledOnExecute,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.FormatPainter)
            ),
            controlInfo.iconName
        );
    }
);

export const getAddressBookControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.AddressBook);

        const recipientViewState: RecipientWellWithFindControlViewState | undefined =
            popoutComposeViewState?.toRecipientWell ??
            getMainWindowComposeViewState()?.toRecipientWell!;

        if (recipientViewState == undefined) {
            throw new Error('Invalid compose view state for Address Book.');
        }

        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.AddressBook, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                onAddressButtonClicked(recipientViewState, targetWindow);
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.AddressBook)
            ),
            controlInfo.iconName
        );
    }
);

export const getCheckNamesControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.CheckNames);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.CheckNames, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            unhandledOnExecute,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.CheckNames)
            ),
            controlInfo.iconName
        );
    }
);

export const getSensitivityControl = computedFn(
    (props: RibbonControlDefinitionProps): RibbonControlProps => {
        const keyTips = constructKeytip(
            [getKeytipString(MailRibbonTabId.Tab_Message)],
            getKeytipString(ActionControlId.Sensitivity),
            true /* hasMenu */
        );
        const menu = { sections: getSensitivitySections(props) };

        return getSensitivityMenu(props.popoutComposeViewState, menu, keyTips, undefined);
    }
);

export const getHighImportanceControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.HighImportance);
        return createMailAppToggleButton(
            getComposeRibbonId(MailRibbonControlId.HighImportance, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            onImportanceToggled(popoutComposeViewState ?? getMainWindowComposeViewState(), 'High'),
            isImportance(popoutComposeViewState ?? getMainWindowComposeViewState(), 'High'),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.HighImportance)
            ),
            controlInfo.iconName
        );
    }
);

export const getLowImportanceControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.LowImportance);
        return createMailAppToggleButton(
            getComposeRibbonId(MailRibbonControlId.LowImportance, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            onImportanceToggled(popoutComposeViewState ?? getMainWindowComposeViewState(), 'Low'),
            isImportance(popoutComposeViewState ?? getMainWindowComposeViewState(), 'Low'),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.LowImportance)
            ),
            controlInfo.iconName
        );
    }
);

export const getFlagUnflagControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.FlagUnflag);
        return createMailAppSplitButton(
            getComposeRibbonId(MailRibbonControlId.FlagUnflag, popoutComposeViewState),
            controlInfo.text,
            controlInfo.ariaText,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            { sections: getFlagUnflagSections(MailRibbonTabId.Tab_Message) },
            onFlagUnflagClicked(),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.FlagUnflag),
                true /* hasMenu */
            ),
            controlInfo.iconName
        );
    }
);

export const getAssignPolicyControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.AssignPolicy);
        return createMailAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.AssignPolicy, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            { sections: getAssignPolicySections(MailRibbonTabId.Tab_Message) },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.AssignPolicy),
                true /* hasMenu */
            ),
            controlInfo.iconName
        );
    }
);

export const getDictateControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Dictate);
        return createMailAppSplitButton(
            getComposeRibbonId(MailRibbonControlId.Dictate, popoutComposeViewState),
            controlInfo.text,
            controlInfo.ariaText,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            emptyMenuDefinition,
            unhandledOnExecute,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.Dictate)
            ),
            controlInfo.iconName
        );
    }
);

export const getImmersiveReaderControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ImmersiveReader);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.ImmersiveReader, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            onShowInImmersiveReaderClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.ImmersiveReader)
            ),
            controlInfo.iconName
        );
    }
);

export const getReadAloudControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ReadAloud);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.ReadAloud, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            unhandledOnExecute,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.ReadAloud)
            ),
            controlInfo.iconName
        );
    }
);

export const getEncryptControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Encrypt);
        return createMailAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.Encrypt, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            {
                sections: [
                    {
                        title: loc(Strings.encrypt_FlyoutTitle),
                        controls: getProtectEncryptSections(
                            popoutComposeViewState ?? getMainWindowComposeViewState()!,
                            targetWindow
                        ),
                    },
                ],
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.Encrypt),
                true /* hasMenu */
            ),
            controlInfo.iconName
        );
    }
);

export const getEncryptSingleButtonControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        return getEncryptSingleButton(
            [getKeytipString(MailRibbonTabId.Tab_Message)],
            popoutComposeViewState,
            targetWindow
        );
    }
);

export const getSaveControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Save);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.Save, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                onSaveDraft(popoutComposeViewState ?? getMainWindowComposeViewState()!);
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Message)],
                getKeytipString(MailRibbonControlId.Save)
            ),
            controlInfo.iconName
        );
    }
);
